import React from "react";
import { Grid3x3, Rows3, LayoutList, Minus, Plus, Scan, RefreshCw, RotateCw, Trash2 } from "lucide-react";
import IconButton from "../common/IconButton";
import { LAYOUT_MODES, SORT_MODES, ZOOM_LEVELS } from "../../constants/previewConfig";

const LAYOUT_ICONS = {
  [LAYOUT_MODES.GRID]: Grid3x3,
  [LAYOUT_MODES.LIST]: Rows3,
  [LAYOUT_MODES.COMPACT]: LayoutList,
};

function WorkspaceToolbar({
  layoutMode,
  setLayoutMode,
  globalZoom,
  setGlobalZoom,
  sortMode,
  setSortMode,
  onReloadAll,
  onRotateAll,
  onClearWorkspace,
  deviceCount,
}) {
  const isFit = globalZoom === "fit";
  const zoomIndex = ZOOM_LEVELS.indexOf(globalZoom);
  const minZoom = ZOOM_LEVELS[0];
  const maxZoom = ZOOM_LEVELS[ZOOM_LEVELS.length - 1];

  // "Fit" is a different scale per device card (and never above 100%), so it
  // has no single position in ZOOM_LEVELS. Leaving Fit, the only steps that
  // are guaranteed to move in the requested direction are the extremes:
  // in -> 100%, out -> the smallest level.
  const zoomOut = () => {
    if (isFit) return setGlobalZoom(minZoom);
    setGlobalZoom(ZOOM_LEVELS[Math.max(0, (zoomIndex === -1 ? ZOOM_LEVELS.length - 1 : zoomIndex) - 1)]);
  };

  const zoomIn = () => {
    if (isFit) return setGlobalZoom(maxZoom);
    setGlobalZoom(ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, (zoomIndex === -1 ? 0 : zoomIndex) + 1)]);
  };

  return (
    <div className="workspace-toolbar">
      <div className="workspace-toolbar-group segmented" role="group" aria-label="Layout mode">
        {Object.values(LAYOUT_MODES).map((mode) => {
          const ModeIcon = LAYOUT_ICONS[mode];
          return (
            <button
              key={mode}
              type="button"
              className={`filter-pill ${layoutMode === mode ? "filter-pill-active" : ""}`}
              aria-pressed={layoutMode === mode}
              onClick={() => setLayoutMode(mode)}
            >
              <ModeIcon size={14} strokeWidth={1.75} aria-hidden="true" />
              {mode[0].toUpperCase() + mode.slice(1)}
            </button>
          );
        })}
      </div>

      <div className="workspace-toolbar-group zoom-control">
        <IconButton label="Zoom out" tooltip="Zoom out" onClick={zoomOut} disabled={globalZoom === minZoom}>
          <Minus size={16} strokeWidth={1.75} aria-hidden="true" />
        </IconButton>
        {/* Keyed so each change replays the readout's tick animation. */}
        <span className="zoom-readout" key={String(globalZoom)}>
          {globalZoom === "fit" ? "Fit" : `${Math.round(globalZoom * 100)}%`}
        </span>
        <IconButton label="Zoom in" tooltip="Zoom in" onClick={zoomIn} disabled={globalZoom === maxZoom}>
          <Plus size={16} strokeWidth={1.75} aria-hidden="true" />
        </IconButton>
        <IconButton label="Fit to screen" tooltip="Fit to screen" onClick={() => setGlobalZoom("fit")}>
          <Scan size={16} strokeWidth={1.75} aria-hidden="true" />
        </IconButton>
      </div>

      <label className="sr-only" htmlFor="sort-mode">
        Sort devices
      </label>
      <select
        id="sort-mode"
        className="brand-select"
        value={sortMode}
        onChange={(event) => setSortMode(event.target.value)}
      >
        <option value={SORT_MODES.DEFAULT}>Sort: Selection order</option>
        <option value={SORT_MODES.WIDTH}>Sort: Width</option>
        <option value={SORT_MODES.CATEGORY}>Sort: Category</option>
        <option value={SORT_MODES.BRAND}>Sort: Brand</option>
        <option value={SORT_MODES.NAME}>Sort: Name</option>
      </select>

      <div className="workspace-toolbar-group">
        <button type="button" className="btn btn-secondary btn-sm" onClick={onReloadAll} disabled={deviceCount === 0}>
          <RefreshCw size={14} strokeWidth={1.75} aria-hidden="true" />
          Reload all
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRotateAll} disabled={deviceCount === 0}>
          <RotateCw size={14} strokeWidth={1.75} aria-hidden="true" />
          Rotate all
        </button>
        <button type="button" className="btn btn-danger btn-sm" onClick={onClearWorkspace} disabled={deviceCount === 0}>
          <Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />
          Clear workspace
        </button>
      </div>
    </div>
  );
}

export default WorkspaceToolbar;
